import { CaretUpDown, MagnifyingGlass, X } from "@phosphor-icons/react"
import { IconButton } from "@tokenterminal/core/ui/button/IconButton"
import { LinkButton } from "@tokenterminal/core/ui/button/LinkButton"
import { SearchField } from "@tokenterminal/core/ui/input/SearchField"
import { Drawer } from "@tokenterminal/core/ui/overlays/modal/Drawer"
import type { LinkProps } from "next/link"
import { useRouter } from "next/router"
import { type CSSProperties, useMemo, useRef, useState } from "react"
import { twMerge } from "tailwind-merge"
import { Button } from "@/components/resources/components/ui/Button"
import { Icon } from "@/components/resources/components/ui/Icon"
import { BASE_BLOG_PATH } from "@/constants/urls"

const headerLinks = [
  { label: "All", href: BASE_BLOG_PATH },
  { label: "Announcements", href: `${BASE_BLOG_PATH}/articles` },
  { label: "Engineering", href: `${BASE_BLOG_PATH}/engineering` },
  { label: "Newsletter", href: `${BASE_BLOG_PATH}/newsletter` },
] as const

{
  /* <ul class="_16u57p10 _16u57p13 _1u0cjk36e _1u0cjk31y6 _1u0cjk31xz _1u0cjk3m3 _1u0cjk3m8 _1u0cjk31fe _1u0cjk31er _12dymb66"><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1u0cjk32y _1noeodu4 ojzmx90" aria-current="page" href="/resources">Home</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/articles">News</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/changelog">Changelog</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/crypto-research">Research</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/interview">Interviews</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/community">Community</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/engineering">Engineering</a></li><li class="_16u57p10 _1u0cjk31fe _1u0cjk31er"><a class="_16u57p10 _1u0cjk33d _1u0cjk32v _1noeodua ojzmx90" href="/resources/weekly-fundamentals">Newsletter</a></li></ul> */
}

const extendedHeaderLinks = [
  { label: "All", href: BASE_BLOG_PATH },
  { label: "News", href: `${BASE_BLOG_PATH}/articles` },
  { label: "Research", href: `${BASE_BLOG_PATH}/crypto-research` },
  { label: "Interviews", href: `${BASE_BLOG_PATH}/interview` },
  { label: "Community", href: `${BASE_BLOG_PATH}/community` },
  { label: "Engineering", href: `${BASE_BLOG_PATH}/engineering` },
  {
    label: "Weekly fundamentals",
    href: `${BASE_BLOG_PATH}/weekly-fundamentals`,
  },
  { label: "Newsletter", href: `${BASE_BLOG_PATH}/newsletter` },
] as const

const NavItem = ({
  href,
  children,
  className,
  ...props
}: LinkProps & { children: React.ReactNode; className?: string }) => {
  const { pathname } = useRouter()

  let isMatchingPath = false

  if (href !== BASE_BLOG_PATH) {
    isMatchingPath = pathname.startsWith(href as string)
  } else {
    isMatchingPath = pathname === href
  }

  return (
    <LinkButton
      href={href}
      variant={isMatchingPath ? "muted" : "ghost"}
      className={twMerge(
        "text-sm no-underline rounded-full",
        isMatchingPath ? "text-fg-default" : "text-fg-secondary",
        className
      )}
      aria-current={isMatchingPath ? "page" : undefined}
      {...props}
    >
      {children}
    </LinkButton>
  )
}

const MobileHeaderNav = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const _searchInputRef = useRef<HTMLInputElement>(null)
  const _searchButtonRef = useRef<HTMLButtonElement>(null)

  const router = useRouter()
  const { pathname } = router
  const [isOpen, setIsOpen] = useState(false)

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      router.push(
        `${BASE_BLOG_PATH}/search?q=${encodeURIComponent(searchQuery.trim())}`
      )
    }
  }

  const activeLink = useMemo(
    () =>
      headerLinks.find(({ href }) => {
        if (href !== BASE_BLOG_PATH) {
          return pathname.startsWith(href as string)
        } else {
          return pathname === href
        }
      }),
    [pathname]
  )

  return (
    <header
      className="block lg:hidden max-w-[1250px] mx-auto relative"
      style={
        {
          "--height": "60px",
        } as CSSProperties
      }
    >
      {/* Default view */}
      <div className="flex items-center justify-between gap-2 py-4 px-3 md:px-6 h-(--height)">
        <div className="flex-1 flex items-center gap-4" />
        {isSearchOpen && (
          <div className="flex-4 flex items-center justify-end">
            <SearchField
              type="text"
              placeholder="Search"
              className="w-full [&_.tt-input-container]:rounded-full [&_.tt-input-container]:px-1"
              autoFocus
              value={searchQuery}
              onChange={(value) => setSearchQuery(value)}
              onKeyDown={handleSearchSubmit}
            />
          </div>
        )}
        <nav className="flex-1 justify-end flex items-center gap-px">
          {!isSearchOpen && (
            <Button
              variant="ghost"
              onClick={() => setIsOpen(!isOpen)}
              aria-label="Toggle menu"
              className="text-fg-secondary"
            >
              {activeLink?.label}
              <Icon as={CaretUpDown} />
            </Button>
          )}
          <IconButton
            variant="ghost"
            onClick={() => setIsSearchOpen(!isSearchOpen)}
            aria-label="Toggle menu"
            className="text-fg-secondary"
            icon={isSearchOpen ? X : MagnifyingGlass}
            label="Search"
          />
        </nav>
      </div>
      <Drawer isOpen={isOpen} onOpenChange={setIsOpen}>
        <Drawer.Header title="Choose a section" />
        <Drawer.Body className="gap-1.5">
          {headerLinks.map(({ label, href }) => (
            <NavItem
              key={label}
              href={href}
              onClick={() => setIsOpen(false)}
              className="rounded-lg"
            >
              {label}
            </NavItem>
          ))}
        </Drawer.Body>
      </Drawer>
    </header>
  )
}

const DesktopHeaderNav = () => {
  const [showMoreLinks, _setShowMoreLinks] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const router = useRouter()

  const linksToShow = showMoreLinks ? extendedHeaderLinks : headerLinks

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && searchQuery.trim()) {
      router.push(
        `${BASE_BLOG_PATH}/search?q=${encodeURIComponent(searchQuery.trim())}`
      )
    }
  }

  return (
    <header
      className="hidden lg:block max-w-[1250px] mx-auto relative"
      style={
        {
          "--height": "60px",
        } as CSSProperties
      }
    >
      {/* Default view */}
      <div className="flex items-center justify-between gap-2 py-4 px-3 md:px-6 h-(--height)">
        <div className="flex-1 flex items-center gap-4" />

        <div className="flex-2 flex items-center justify-center relative">
          <nav className="">
            <div className="flex flex-row gap-0.5 list-none">
              {linksToShow.map(({ label, href }) => (
                <NavItem
                  key={label}
                  href={href}
                  className={showMoreLinks ? "text-xs px-3" : "text-sm"}
                >
                  {label}
                </NavItem>
              ))}
              {/* More button */}
              {/* <IconButton
                label={showMoreLinks ? "Less" : "More"}
                icon={showMoreLinks ? CaretLeft : CaretRight}
                variant="ghost"
                onClick={() => setShowMoreLinks(!showMoreLinks)}
                className="text-fg-secondary/50 rounded-full"
              /> */}
            </div>
          </nav>
        </div>
        <div className="flex-1 flex items-center justify-end [&_.tt-input-container]:rounded-full [&_.tt-input-container]:px-1">
          <SearchField
            type="text"
            placeholder="Search"
            className="max-w-[230px]"
            value={searchQuery}
            onChange={(value) => setSearchQuery(value)}
            onKeyDown={handleSearchSubmit}
          />
        </div>
      </div>
    </header>
  )
}

export const BlogHeader = () => {
  return (
    <div
      className="w-full z-10 border-t border-b border-border-default"
      style={{
        borderTopStyle: "dashed",
        borderBottomStyle: "dashed",
      }}
    >
      <DesktopHeaderNav />
      <MobileHeaderNav />
    </div>
  )
}

BlogHeader.displayName = "BlogHeader"
